import {
  Anchor,
  Badge,
  Group,
  LoadingOverlay,
  Pagination,
  ScrollArea,
  Select,
  Table,
} from "@mantine/core";
import { DateField, List, ShowButton } from "@refinedev/mantine";
import { useTable } from "@refinedev/react-table";
import { ColumnDef, Header } from "@tanstack/react-table";
import AppliedFilters from "components/common/list/AppliedFilters";
import Headers from "components/common/list/Headers";
import { Rows } from "components/common/list/Rows";
import {
  TeaAddonOperation,
  TeaAddonRenewalRun,
} from "entities/tea_addon_renewal_run";
import { useMemo } from "react";
import { Link } from "react-router-dom";
import { Row } from "react-table";

export const TeaAddonRenewalRunList: React.FC = () => {
  const columns = useMemo<ColumnDef<TeaAddonRenewalRun>[]>(
    () => [
      {
        id: "id",
        header: "ID",
        accessorKey: "id",
      },
      {
        id: "range_start_at",
        header: "Range Start At",
        accessorKey: "range_start_at",
        cell: function render({ getValue }) {
          const date = getValue<TeaAddonRenewalRun["range_start_at"]>();

          return <DateField value={date} format="DD/MM/YYYY HH:mm:ss" />;
        },
      },
      {
        id: "range_end_at",
        header: "Range End At",
        accessorKey: "range_end_at",
        cell: function render({ getValue }) {
          const date = getValue<TeaAddonRenewalRun["range_end_at"]>();

          return <DateField value={date} format="DD/MM/YYYY HH:mm:ss" />;
        },
      },
      {
        id: "started_at",
        header: "Started At",
        accessorKey: "started_at",
        cell: function render({ getValue }) {
          const date = getValue<TeaAddonRenewalRun["started_at"]>();
          if (!date) return <div />;

          return <DateField value={date} format="DD/MM/YYYY HH:mm:ss" />;
        },
      },
      {
        id: "ended_at",
        header: "Ended At",
        accessorKey: "ended_at",
        cell: function render({ getValue }) {
          const date = getValue<TeaAddonRenewalRun["started_at"]>();
          if (!date) return <div />;

          return <DateField value={date} format="DD/MM/YYYY HH:mm:ss" />;
        },
      },
      {
        id: "succeeded_operations_count",
        header: "Succeeded Operations",
        accessorKey: "succeeded_operations_count",
        cell: function render({ getValue }) {
          const total =
            getValue<TeaAddonRenewalRun["succeeded_operations_count"]>();

          return <Badge color="green">{total}</Badge>;
        },
      },
      {
        id: "failed_operations_count",
        header: "Failed Operations",
        accessorKey: "failed_operations_count",
        cell: function render({ getValue }) {
          const total =
            getValue<TeaAddonRenewalRun["failed_operations_count"]>();

          return <Badge color="red">{total}</Badge>;
        },
      },
      {
        id: "created_at",
        header: "Created At",
        accessorKey: "created_at",
        cell: function render({ getValue }) {
          const date = getValue<TeaAddonRenewalRun["created_at"]>();

          return <DateField value={date} format="DD/MM/YYYY HH:mm:ss" />;
        },
      },
      {
        id: "actions",
        header: "Actions",
        accessorKey: "id",
        cell: function render({ getValue }) {
          return (
            <Group spacing="xs" noWrap>
              <ShowButton hideText recordItemId={getValue() as number} />
            </Group>
          );
        },
      },
    ],
    [],
  );

  const {
    getHeaderGroups,
    getRowModel,
    setOptions,
    getFlatHeaders,
    refineCore: {
      setCurrent,
      pageCount,
      current,
      tableQueryResult: { data: tableData, isFetching },
    },
  } = useTable({
    columns,
    refineCoreProps: {
      sorters: {
        initial: [
          {
            field: "id",
            order: "desc",
          },
        ],
      },
      pagination: {
        pageSize: 50,
      },
    },
  });

  return (
    <List>
      <AppliedFilters<TeaAddonRenewalRun> headers={getFlatHeaders()} />
      <ScrollArea>
        <Table highlightOnHover pos="relative">
          <LoadingOverlay visible={isFetching} />
          <Headers<TeaAddonRenewalRun> headerGroups={getHeaderGroups()} />
          <Rows<TeaAddonRenewalRun>
            rows={getRowModel().rows}
            rowStyle={(row: Row<TeaAddonRenewalRun>) =>
              row.original.failed_operations_count !== 0
                ? { background: "rgba(198, 40, 40, 0.1)" }
                : {}
            }
          />
        </Table>
      </ScrollArea>
      <br />
      <Pagination
        position="right"
        total={pageCount}
        page={current}
        onChange={setCurrent}
      />
    </List>
  );
};
